import React, { Fragment, useState } from "react";
import {
  TableHead,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableContainer,
  IconButton,
  Stack,
  TextField,
  Button,
  Typography,
  styled,
  TableSortLabel,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { decimalFormatter } from "shared/utils";

import { DELETE_OAR, GET_OAR_TEMPLATE, REPLACE_OARS } from "graphql/OarQueries";
import { GET_PRESCRIPTION_TEMPLATE } from "graphql/PrescriptionQueries";

const StyledTableHead = styled(TableHead)`
  background-color: ${(props) => props.theme.palette.grey[100]};
`;

const StyledTableHeaderCell = styled(TableCell)`
  font-weight: bold;
  border-bottom-width: 0px;
`;

const StyledTable = styled(Table)`
  max-width: 800px;
  border-spacing: 0 16px;
  border-collapse: separate;
`;

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-head {
    background-color: black;
    color: white;
  }
  &.MuiTableCell-body {
    font-size: 14;
    padding-top: 4px;
    padding-bottom: 4px;
    border-top-width: 1px;
    border-color: ${(props) => props.theme.palette.grey[300]};
    border-style: solid;
    border-right-width: 0px;
    border-left-width: 0px;
    &:first-of-type {
      border-left-width: 1px;
    }
    &:last-of-type {
      border-right-width: 1px;
    }
  }
`;

interface OarInterface {
  id: string;
  name: string;
  organ: string;
}

interface OarColumnInterface {
  attribute: string;
  label: string;
}

interface Props {
  oars: OarInterface[];
  editOar: any;
  prescriptionId: string;
  isActive: boolean;
  inBulkEdit: boolean;
}

interface OarRowProps {
  oar: OarInterface;
  oarKey: string;
  onClick: () => void;
  onDelete: () => void;
  isActive: boolean;
}

const OAR_TABLE_COLUMNS: OarColumnInterface[] = [
  { label: "Organ", attribute: "name" },
  { label: "Constraints", attribute: "constraints" },
  { label: "Edit", attribute: "edit" },
  { label: "Delete", attribute: "delete" },
];

const OarRow = ({
  oar,
  oarKey,
  onClick,
  onDelete,
  isActive,
}: OarRowProps): React.ReactElement => {
  const oarConstraints = constraintsConvert(oar);
  return (
    <Fragment key={oar.organ}>
      <TableRow data-test-id={`oar-row-${oarKey}`} key={oarKey}>
        <StyledTableCell data-test-id={`organ-${oarKey}`}>
          {oar.organ}
        </StyledTableCell>
        <StyledTableCell data-test-id={`oarConstraints-${oarKey}`}>
          {oarConstraints.map((constraint: any, index: number) => (
            <div key={`${constraint}-${index}`}>{constraint}</div>
          ))}
        </StyledTableCell>
        <StyledTableCell align="center">
          <IconButton
            disabled={!isActive}
            data-test-id={`oarEdit-${oarKey}`}
            onClick={onClick}
            size="large"
          >
            <EditIcon color="primary" />
          </IconButton>
        </StyledTableCell>
        <StyledTableCell align="center">
          <IconButton
            disabled={!isActive}
            data-test-id={`oarDelete-${oarKey}`}
            onClick={onDelete}
            size="large"
          >
            <DeleteIcon color="error" />
          </IconButton>
        </StyledTableCell>
      </TableRow>
    </Fragment>
  );
};

export const constraints = (item: any) => {
  let constraints: string[] = [];
  if (item.constraints) {
    constraints = item.constraints.map((constraint: any) => {
      if (constraint.is_volume_data) {
        return `V${decimalFormatter(constraint.first_value) || " "}${constraint.first_value_unit} ${
          constraint.operator
        } ${(constraint.second_value && constraint.second_value.join("/")) || " "}${
          constraint.second_value_unit
        }`;
      } else {
        return `${constraint.doseto_prefix_operator || "D"}${decimalFormatter(constraint.first_value) || " "}${constraint.first_value_unit} ${
          constraint.operator
        } ${(constraint.second_value && constraint.second_value.join("/")) || " "}${
          constraint.second_value_unit
        }`;
      }
    });
  }
  if (item.max_value && item.max_value.length) {
    constraints = [
      ...constraints,
      `Max ≤ ${(item.max_value && item.max_value.join("/")) || " "}${
        item.max_value_unit
      }`,
    ];
  }
  if (item.mean_value && item.mean_value.length) {
    constraints = [
      ...constraints,
      `Mean ≤ ${(item.mean_value && item.mean_value.join("/")) || " "}${
        item.mean_value_unit
      }`,
    ];
  }
  if (item.alara) constraints = ["ALARA"];
  return constraints;
};

export const constraintsConvert = (item: any) => {
  let constraints: string[] = [];
  if (item.volumeConstraintData)
    constraints = [
      ...item.volumeConstraintData.map(
        (vcd: any): string =>
          `V${decimalFormatter(vcd.firstValue) || " "}${vcd.firstValueUnit} ${
            vcd.operator
          } ${(vcd.secondValue && vcd.secondValue.join("/")) || " "}${
            vcd.secondValueUnit
          }`,
      ),
    ];
  if (item.dConstraintData)
    constraints = [
      ...constraints,
      ...item.dConstraintData.map(
        (dcd: any): string =>
          `${dcd.dosetoPrefixOperator || "D"}${decimalFormatter(dcd.firstValue) || " "}${dcd.firstValueUnit} ${
            dcd.operator
          } ${(dcd.secondValue && dcd.secondValue.join("/")) || " "}${
            dcd.secondValueUnit
          }`,
      ),
    ];

  if (item.maxValue && item.maxValue.length) {
    constraints = [
      ...constraints,
      `Max ≤ ${(item.maxValue && item.maxValue.join("/")) || " "}${
        item.maxValueUnit
      }`,
    ];
  }
  if (item.meanValue && item.meanValue.length) {
    constraints = [
      ...constraints,
      `Mean ≤ ${(item.meanValue && item.meanValue.join("/")) || " "}${
        item.meanValueUnit
      }`,
    ];
  }
  if (item.alara) constraints = ["ALARA"];
  return constraints;
};

const sortOarsByOrgan = (
  oars: OarInterface[],
  orderDirection: string,
): OarInterface[] => {
  const sortedOars = [...oars].sort((a, b) => {
    if (!a.organ || !b.organ) return 0;
    return orderDirection === "desc"
      ? b.organ.localeCompare(a.organ)
      : a.organ.localeCompare(b.organ);
  });
  return sortedOars;
};

const OarTable = (props: Props) => {
  const { oars, editOar, prescriptionId, isActive, inBulkEdit } = props;
  const [oarReplaceTemplateId, setOarReplaceTemplateId] = useState("");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");

  // Sort the OARs by name before rendering
  const sortedOars = sortOarsByOrgan(oars, orderDirection);

  const [deleteOar] = useMutation(DELETE_OAR, {
    refetchQueries: [
      {
        query: GET_OAR_TEMPLATE,
        variables: { prescriptionId: prescriptionId },
      },
      { query: GET_PRESCRIPTION_TEMPLATE, variables: { id: prescriptionId } },
    ],
  });

  const [replaceOARs] = useMutation(REPLACE_OARS, {
    refetchQueries: [
      {
        query: GET_OAR_TEMPLATE,
        variables: { prescriptionId: prescriptionId },
      },
      { query: GET_PRESCRIPTION_TEMPLATE, variables: { id: prescriptionId } },
    ],
    onCompleted: (results) => {
      setOarReplaceTemplateId("");
    },
    onError: (error) => {
      alert(error.message || "Something went wrong");
    },
  });

  const onReplaceOAR = () => {
    replaceOARs({
      variables: {
        prescriptionId: prescriptionId,
        templateId: oarReplaceTemplateId,
        isSelectedForBulkEdit: inBulkEdit,
      },
    });
  };

  const onDeleteOar = (index: number) => {
    deleteOar({
      variables: {
        prescriptionId: prescriptionId,
        index: index,
      },
    });
  };
  return (
    <TableContainer>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography>Replace with:</Typography>
        <TextField
          placeholder="Template ID"
          value={oarReplaceTemplateId}
          disabled={!isActive}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setOarReplaceTemplateId(event.target.value);
          }}
        />
        <Button disabled={!isActive} variant="contained" onClick={onReplaceOAR}>
          Replace OARs
        </Button>
      </Stack>

      <StyledTable aria-label="customized table">
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>
        <StyledTableHead>
          <TableRow>
            {OAR_TABLE_COLUMNS.map((header, index) => (
              <StyledTableHeaderCell
                align={index > 1 ? "center" : "left"}
                key={`oar-header-${index}`}
              >
                {header.attribute === "name" ? (
                  <TableSortLabel
                    active={true}
                    direction={orderDirection}
                    onClick={() =>
                      setOrderDirection(
                        orderDirection === "asc" ? "desc" : "asc",
                      )
                    }
                  >
                    {header.label}
                  </TableSortLabel>
                ) : (
                  <>{header.label}</>
                )}
              </StyledTableHeaderCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {sortedOars.map((oar: OarInterface, index: number) => {
            // Need the oar index as the update calls in the Voluming.tsx file use index instead of id (should replace later)
            const oarIndex = oars.indexOf(oar);
            return (
              <OarRow
                key={`oar-row-${index}`}
                oarKey={`oar-row-${oarIndex}`}
                oar={oar}
                onClick={() => editOar(oarIndex)}
                onDelete={() => onDeleteOar(oarIndex)}
                isActive={isActive}
              />
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default OarTable;
